import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Check, ExternalLink, PlayCircle } from 'lucide-react';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';

const PromoWorkbook = () => {
  const { t, i18n } = useTranslation();
  const { events: gaEvents } = useGoogleAnalytics();
  const [isDemoModalOpen, setIsDemoModalOpen] = React.useState(false);
  const currentLang = i18n.language || 'en';
  
  const handleBuy = (lang) => {
    gaEvents.buyWorkbookClick(lang);
    const url = lang === 'en' 
      ? 'https://buy.stripe.com/6oU00j2jC84N7KN3Pi0oM00'
      : 'https://buy.stripe.com/8x2aEX9M4gBj6GJfy00oM01';
    window.open(url, '_blank');
  };

  const handleWatchDemo = () => {
    gaEvents.watchWorkbookDemo(currentLang);
    setIsDemoModalOpen(true);
  };

  return (
    <section className="pt-2 md:pt-6 pb-12 md:pb-20 px-4 sm:px-6 relative overflow-hidden bg-ink text-white w-full">
      {/* Decorative background gradients matching the homepage accent theme */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/10 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent/10 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>

      <div className="max-w-screen-xl mx-auto relative z-10 flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
        
        <div className="flex-1 space-y-8 text-center lg:text-left relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent-400 ring-1 ring-accent/20 text-sm font-bold uppercase tracking-wider">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-400"></span>
              </span>
              {t('promo_workbook.new_release')}
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {t('promo_workbook.title')}
            </h2>
            
            <p className="text-lg text-white/70 max-w-2xl leading-relaxed mx-auto lg:mx-0">
              {t('promo_workbook.description')}
            </p>
            
            <ul className="space-y-3 text-white/90 text-left mx-auto lg:mx-0 max-w-2xl">
              <li className="flex items-start gap-3">
                <Check className="w-6 h-6 text-accent-400 flex-shrink-0" />
                <span>{t('promo_workbook.benefit_1')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-6 h-6 text-accent-400 flex-shrink-0" />
                <span>{t('promo_workbook.benefit_2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-6 h-6 text-accent-400 flex-shrink-0" />
                <span>{t('promo_workbook.benefit_3')}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-6 h-6 text-accent-400 flex-shrink-0" />
                <span>{t('promo_workbook.benefit_4')}</span>
              </li>
            </ul>
          </div>
          
          <div className="w-full lg:w-auto flex flex-col gap-4 bg-surface/80 backdrop-blur-sm p-6 md:p-8 rounded-2xl border border-line shadow-xl relative z-10">
            <div className="flex justify-center mb-2">
              <div className="w-16 h-16 bg-accent/10 ring-1 ring-accent/20 rounded-full flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-accent-400" />
              </div>
            </div>
            <div className="text-center mb-2">
              <p className="text-accent-400 text-xs font-semibold uppercase tracking-wider mb-1">
                {t('promo_workbook.get_it_now_by_email')}
              </p>
              <h3 className="text-white font-bold text-xl">
                {t('promo_workbook.get_it_now')}
              </h3>
            </div>
            <button 
              onClick={() => handleBuy('en')}
              className="w-full sm:w-72 bg-cta hover:bg-cta-hover text-ink font-bold py-4 px-6 rounded-xl shadow-[0_8px_30px_rgba(255,176,0,0.28)] hover:shadow-[0_10px_40px_rgba(255,176,0,0.45)] flex items-center justify-between transition-all duration-200 transform hover:-translate-y-1 group"
            >
              <span>{t('promo_workbook.buy_en')}</span>
              <ExternalLink className="w-5 h-5 group-hover:scale-110 transition-transform" />
            </button>
            
            <button 
              onClick={() => handleBuy('es')}
              className="w-full sm:w-72 bg-white/5 hover:bg-white/10 text-white font-bold py-4 px-6 rounded-xl border border-white/10 hover:border-white/20 shadow-lg flex items-center justify-between transition-all duration-200 transform hover:-translate-y-1 group"
            >
              <span>{t('promo_workbook.buy_es')}</span>
              <ExternalLink className="w-5 h-5 group-hover:scale-110 transition-transform" />
            </button>
            
            <p className="text-center text-white/50 text-xs mt-2">
               {t('promo_workbook.secure_checkout')}
            </p>

            <button
              onClick={() => handleWatchDemo()}
              className="mt-4 w-full sm:w-72 mx-auto text-white/80 hover:text-white underline underline-offset-4 decoration-white/50 hover:decoration-white font-medium text-sm transition-colors py-2 flex items-center justify-center gap-2 group"
            >
              <PlayCircle className="w-4 h-4" />
              <span>{t('promo_workbook.watch_demo')}</span>
            </button>
          </div>

      </div>

      {/* Workbook preview */}
      <div className="max-w-screen-xl mx-auto relative z-10 mt-14 md:mt-20">
        <img
          src="/zendesk_workbook_image_easyapp.png"
          alt={t('promo_workbook.image_alt')}
          loading="lazy"
          className="w-full h-auto rounded-2xl border border-line shadow-2xl"
        />
      </div>

      {/* Demo Modal */}
      {isDemoModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 bg-ink/80 backdrop-blur-sm animate-fade-in" onClick={() => setIsDemoModalOpen(false)}>
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-5xl overflow-hidden animate-zoom-in relative aspect-video flex flex-col" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setIsDemoModalOpen(false)}
              className="absolute top-4 right-4 z-10 text-white/50 hover:text-white bg-black/40 hover:bg-black/60 p-2 rounded-full transition-all duration-200"
            >
               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <iframe 
               src={currentLang.startsWith('es') ? "https://drive.google.com/file/d/1KuVoZicxcoiZQdFLV7Dlzuzeqm5SfH28/preview" : "https://drive.google.com/file/d/14rnEE_SQX7U0XKjE-SLnWD3shadM8i1L/preview"} 
               className="w-full h-full border-0"
               allow="autoplay"
               title="Demo Video"
            ></iframe>
          </div>
        </div>
      )}
    </section>
  );
};

export default PromoWorkbook;
